/* eslint-disable max-len */
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { initializeOCRSDK, extractOCRDocument } from "@rakbank/react-ocr-web";
import { log } from "../../utils/loggger";
import { DOCUMENT_TYPES, OCR_DATA_FETCH_TIMEOUT, OCR_FETCHED_PAGE_COUNT } from "../../constants";
import {
  OCR_PROCESS_DOCUMENT_REQUEST,
  setOcrData,
  setOcrDataLoading,
  setOcrError
} from "../actions/ocrData";
import { getOcrError } from "../selectors/ocrData";
import { updateProspect } from "../actions/appConfig";
import { NotificationsManager } from "../../components/Notification";

// OCR configuration from environment variables for security
const OCR_CONFIG = {
  baseUrl: process.env.REACT_APP_OCR_API_URL,
  apiKey: process.env.REACT_APP_OCR_API_KEY,
  clientSecret: process.env.REACT_APP_OCR_CLIENT_SECRET_KEY,
  clientId: process.env.REACT_APP_OCR_CLIENT_ID,
  resourceType: process.env.REACT_APP_OCR_RESOURCE_TYPE
};

// Cache for OCR token to avoid multiple initialization calls
let ocrTokenCache = null;

/**
 * Worker saga for initializing OCR SDK with caching
 */
function* initializeOcrSaga() {
  try {
    // Return cached token if available
    if (ocrTokenCache) {
      return ocrTokenCache;
    }

    // Validate OCR configuration
    if (!OCR_CONFIG.baseUrl || !OCR_CONFIG.apiKey || !OCR_CONFIG.clientSecret) {
      throw new Error("OCR configuration missing");
    }

    // Initialize OCR SDK using call effect
    const result = yield call(initializeOCRSDK, {
      baseUrl: OCR_CONFIG.baseUrl,
      clientSecret: OCR_CONFIG.clientSecret,
      apiKey: OCR_CONFIG.apiKey,
      clientId: OCR_CONFIG.clientId,
      resourceType: OCR_CONFIG.resourceType
    });

    // Extract token and traceId from result and cache it
    const { token, traceId } = result;
    ocrTokenCache = { token, traceId };

    return ocrTokenCache;
  } catch (error) {
    log("OCR SDK initialization error:", error);
    throw error;
  }
}

/**
 * Worker saga for processing documents with OCR
 * @param {Object} action - The dispatched action containing document details
 */
export function* processDocumentWithOcrSaga({ payload }) {
  const {
    frontSideFile,
    backSideFile,
    file,
    docType,
    timeout = OCR_DATA_FETCH_TIMEOUT,
    signatoryId = ""
  } = payload;
  try {
    yield put(setOcrDataLoading(docType, true));

    const { token, traceId } = yield call(initializeOcrSaga);

    const ocrResult = yield call(extractOCRDocument, {
      baseUrl: OCR_CONFIG.baseUrl,
      token,
      traceId,
      apiKey: OCR_CONFIG.apiKey,
      documentType: docType,
      ...(frontSideFile && { frontFile: frontSideFile }),
      ...(backSideFile && { backFile: backSideFile }),
      ...(file && { file }),
      client: OCR_CONFIG.clientId,
      abortController: "",
      timeoutMs: timeout,
      num_pages: OCR_FETCHED_PAGE_COUNT
    });

    // Handle multiple objects in OCR result
    let ocrExtractedData = {};

    if (Array.isArray(ocrResult)) {
      // Multiple objects detected - merge or select the best one
      ocrExtractedData = {
        data: ocrResult.reduce((acc, item) => {
          return {
            ...acc,
            ...item.extracted_data.data
          };
        }, {})
      };
    } else {
      ocrExtractedData = ocrResult?.extracted_data || {};
    }

    if (docType === DOCUMENT_TYPES.TRADE_LICENSE) {
      yield put(
        updateProspect({
          "prospect.organizationInfo.tlOcrExtract": JSON.stringify(ocrExtractedData)
        })
      );
    }

    yield put(setOcrData(ocrResult?.document_type || docType, ocrExtractedData));

    // Check if there's already an error for this signatoryId
    const ocrErrors = yield select(getOcrError);
    const existingError = ocrErrors[signatoryId];

    // Only clear the error if there's no existing error or if the existing error is for the same docType
    if (!existingError || existingError.key === docType) {
      yield put(setOcrError(docType, "", signatoryId));
    }

    return { success: true, data: ocrExtractedData };
  } catch (error) {
    if (docType === DOCUMENT_TYPES.TRADE_LICENSE) {
      yield put(
        updateProspect({
          "prospect.organizationInfo.tlOcrExtract": JSON.stringify({
            ocrExtractedData: error?.message
          })
        })
      );
    }
    yield put(setOcrError(docType, error, signatoryId));
    const notificationOptions = { title: "Oops", message: error?.message };
    NotificationsManager.add(notificationOptions);
    return { success: false, error: error?.message };
  } finally {
    yield put(setOcrDataLoading(docType, false));
  }
}

/**
 * Root saga for OCR data processing
 */
export default function* ocrDataSaga() {
  yield all([takeLatest(OCR_PROCESS_DOCUMENT_REQUEST, processDocumentWithOcrSaga)]);
}
